/**
 * GreetingBlock — the "Hey, {firstName} 👋" hero strip rendered above
 * Home and Jobs pages (Mobbin "Workspace home" pattern: Deel web Jan 2026 12.png).
 *
 * Composition:
 *   • Centered greeting heading with first-name + waving hand emoji.
 *   • Sub-line ("What do you want to do today?")
 *   • Segmented chip selector (filter intents).
 *   • Job-focused hero search — opens the Jobs board only (AI lives on `/agent`).
 *
 * Theme stays on our `--brand-purple` (#6C47FF) — we do NOT copy Deel's purples.
 *
 * The chip selector emits `onIntentChange(intentId)`. Callers can ignore it
 * for now; in TASK 1-3 the Home page wires it to actually filter content.
 */
import { Search } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';


export type GreetingIntent = 'all' | 'jobs' | 'optimize' | 'prepare' | 'activity';

export interface GreetingBlockProps {
  /** Just the first name — `useUserProfile().profile?.displayName.split(' ')[0]`. */
  firstName: string | null;
  /** Default-active chip id. */
  initialIntent?: GreetingIntent;
  /** Optional callback when an intent chip is clicked. */
  onIntentChange?: (intent: GreetingIntent) => void;
  /** Optional callback when the user submits the search box. */
  onSearch?: (query: string) => void;
}


const INTENT_CHIPS: Array<{ id: GreetingIntent; labelKey: string; fallback: string }> = [
  { id: 'all',      labelKey: 'shell.greeting.intent.all',      fallback: 'All'         },
  { id: 'jobs',     labelKey: 'shell.greeting.intent.jobs',     fallback: 'Find jobs'   },
  { id: 'optimize', labelKey: 'shell.greeting.intent.optimize', fallback: 'Optimize CV' },
  { id: 'prepare',  labelKey: 'shell.greeting.intent.prepare',  fallback: 'Prepare'     },
  { id: 'activity', labelKey: 'shell.greeting.intent.activity', fallback: 'My activity' },
];


const GreetingBlock: React.FC<GreetingBlockProps> = ({
  firstName,
  initialIntent = 'all',
  onIntentChange,
  onSearch,
}) => {
  const { t } = useTranslation();
  const [intent, setIntent] = useState<GreetingIntent>(initialIntent);
  const [query, setQuery] = useState('');

  const greetName = (firstName || '').trim() || t('shell.greeting.thereFallback', 'there');

  const setActiveIntent = (id: GreetingIntent) => {
    setIntent(id);
    onIntentChange?.(id);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(query.trim());
  };

  return (
    <section
      className="w-full max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-10 pb-4 sm:pb-6 flex flex-col items-center gap-4 sm:gap-5"
      aria-labelledby="greeting-heading"
    >
      {/* ── Heading ── */}
      <div className="text-center">
        <h1
          id="greeting-heading"
          className="text-[clamp(1.5rem,3.5vw,2rem)] font-bold vscode-text-primary leading-tight"
        >
          {t('shell.greeting.hello', 'Hey, {{name}}', { name: greetName })}
          <span className="ml-2" aria-hidden>👋</span>
        </h1>
        <p className="mt-1.5 text-sm vscode-text-secondary">
          {t('shell.greeting.subtitle', 'What do you want to do today?')}
        </p>
      </div>

      {/* ── Chip selector ── */}
      <div
        role="tablist"
        aria-label={t('shell.greeting.intentAria', 'Intent filter')}
        className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-center"
      >
        {INTENT_CHIPS.map((c) => {
          const active = c.id === intent;
          return (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setActiveIntent(c.id)}
              className={active ? 'chip-filter-active' : 'chip-filter'}
            >
              {t(c.labelKey, c.fallback)}
            </button>
          );
        })}
      </div>

      {/* ── Big search input ── */}
      <form onSubmit={handleSubmit} className="w-full max-w-xl" role="search">
        <label
          className="flex items-center gap-2 px-4 h-12 rounded-2xl border vscode-border
            vscode-bg-elevated focus-within:border-[var(--brand-purple,#6C47FF)] focus-within:shadow-[0_0_0_3px_rgba(108,71,255,0.15)]
            transition-all"
        >
          <Search className="w-5 h-5 text-[var(--brand-purple,#6C47FF)] shrink-0" aria-hidden />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t(
              'shell.greeting.searchPlaceholder',
              'Search jobs…',
            )}
            className="flex-1 min-w-0 bg-transparent outline-none border-0 focus:ring-0
              vscode-text-primary placeholder:vscode-text-tertiary text-sm"
            aria-label={t('shell.greeting.searchAria', 'Search')}
          />
        </label>
      </form>
    </section>
  );
};

export default GreetingBlock;
